import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Button from '../common/Button';
import { createCommunity } from '../../api/communities';

/**
 * CommunityForm component for creating a new community
 * @param {Object} props - Component props
 * @param {Function} props.onSuccess - Callback for successful creation
 * @param {Function} props.onCancel - Callback for cancellation
 * @returns {JSX.Element} CommunityForm component
 */
const CommunityForm = ({ onSuccess, onCancel }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    isPrivate: false,
    rules: [{ title: '', description: '' }],
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  
  // Available categories
  const categories = [
    'Technology',
    'Business',
    'Health',
    'Science',
    'Education',
    'Entertainment',
    'Sports',
    'Gaming',
    'Arts',
    'Food',
    'Travel',
    'Finance',
    'Fashion',
    'Photography',
    'Music',
    'Other',
  ];
  
  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    
    // Clear field error
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };
  
  // Handle rule change
  const handleRuleChange = (index, field, value) => {
    const updatedRules = [...formData.rules];
    updatedRules[index] = {
      ...updatedRules[index],
      [field]: value,
    };
    
    setFormData({
      ...formData,
      rules: updatedRules,
    });
    
    // Clear rule errors
    if (errors[`rules[${index}].${field}`]) {
      setErrors({
        ...errors,
        [`rules[${index}].${field}`]: '',
      });
    }
  };
  
  // Add a new rule
  const addRule = () => {
    setFormData({
      ...formData,
      rules: [...formData.rules, { title: '', description: '' }],
    });
  };
  
  // Remove a rule
  const removeRule = (index) => {
    const updatedRules = [...formData.rules];
    updatedRules.splice(index, 1);
    
    setFormData({
      ...formData,
      rules: updatedRules.length > 0 ? updatedRules : [{ title: '', description: '' }],
    });
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Community name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Community name must be at least 3 characters long';
    } else if (formData.name.length > 50) {
      newErrors.name = 'Community name must be less than 50 characters long';
    }
    
    // Description validation
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length < 10) {
      newErrors.description = 'Description must be at least 10 characters long';
    }
    
    // Category validation
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    
    // Validate rules if they have content
    formData.rules.forEach((rule, index) => {
      if (rule.title && !rule.description) {
        newErrors[`rules[${index}].description`] = 'Rule description is required';
      }
      if (!rule.title && rule.description) {
        newErrors[`rules[${index}].title`] = 'Rule title is required';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      setFormError('');
      
      // Filter out empty rules
      const cleanedData = {
        ...formData,
        rules: formData.rules.filter(
          (rule) => rule.title.trim() && rule.description.trim()
        ),
      };
      
      const response = await createCommunity(cleanedData);
      
      // Call success callback or redirect
      if (onSuccess) {
        onSuccess(response.data.community);
      } else {
        navigate(`/communities/${response.data.community.slug}`);
      }
    } catch (error) {
      console.error('Error creating community:', error);
      setFormError(
        error.response?.data?.message || 'Failed to create community. Please try again.'
      );
      
      // Set field-specific errors if available
      if (error.response?.data?.errors) {
        setErrors({
          ...errors,
          ...error.response.data.errors,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Form error */}
      {formError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {formError}
        </div>
      )}
      
      {/* Community name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Community Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`w-full px-3 py-2 border ${
            errors.name ? 'border-red-500' : 'border-gray-300'
          } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
          placeholder="e.g., Web Development"
        />
        {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
      </div>
      
      {/* Community description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={4}
          className={`w-full px-3 py-2 border ${
            errors.description ? 'border-red-500' : 'border-gray-300'
          } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
          placeholder="Describe what your community is about..."
        ></textarea>
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">{errors.description}</p>
        )}
      </div>
      
      {/* Category */}
      <div>
        <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
          Category <span className="text-red-500">*</span>
        </label>
        <select
          id="category"
          name="category"
          value={formData.category}
          onChange={handleChange}
          className={`w-full px-3 py-2 border ${
            errors.category ? 'border-red-500' : 'border-gray-300'
          } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
        >
          <option value="">Select a category</option>
          {categories.map((category) => (
            <option key={category} value={category.toLowerCase()}>
              {category}
            </option>
          ))}
        </select>
        {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
      </div>
      
      {/* Privacy setting */}
      <div className="flex items-center">
        <input
          type="checkbox"
          id="isPrivate"
          name="isPrivate"
          checked={formData.isPrivate}
          onChange={handleChange}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="isPrivate" className="ml-2 block text-sm text-gray-700">
          Make this community private
        </label>
      </div>
      <p className="text-sm text-gray-500 -mt-4">
        Private communities are only visible to members and require approval to join.
      </p>
      
      {/* Community rules */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-sm font-medium text-gray-700">Community Rules</h3>
          <Button type="button" variant="light" size="xs" onClick={addRule}>
            Add Rule
          </Button>
        </div>
        
        <div className="space-y-4">
          {formData.rules.map((rule, index) => (
            <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-sm font-medium text-gray-700">Rule {index + 1}</h4>
                <button
                  type="button"
                  onClick={() => removeRule(index)}
                  className="text-red-600 hover:text-red-800 text-sm"
                  disabled={formData.rules.length === 1}
                >
                  Remove
                </button>
              </div>
              
              {/* Rule title */}
              <div className="mb-2">
                <input
                  type="text"
                  value={rule.title}
                  onChange={(e) => handleRuleChange(index, 'title', e.target.value)}
                  className={`w-full px-3 py-2 border ${
                    errors[`rules[${index}].title`] ? 'border-red-500' : 'border-gray-300'
                  } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                  placeholder="Rule title"
                />
                {errors[`rules[${index}].title`] && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors[`rules[${index}].title`]}
                  </p>
                )}
              </div>
              
              {/* Rule description */}
              <div>
                <textarea
                  value={rule.description}
                  onChange={(e) => handleRuleChange(index, 'description', e.target.value)}
                  rows={2}
                  className={`w-full px-3 py-2 border ${
                    errors[`rules[${index}].description`] ? 'border-red-500' : 'border-gray-300'
                  } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                  placeholder="Rule description"
                ></textarea>
                {errors[`rules[${index}].description`] && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors[`rules[${index}].description`]}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Form actions */}
      <div className="flex justify-end space-x-3">
        {onCancel && (
          <Button type="button" variant="light" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          isLoading={isSubmitting}
          isDisabled={isSubmitting}
        >
          Create Community
        </Button>
      </div>
    </form>
  );
};

CommunityForm.propTypes = {
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
};

export default CommunityForm;